import React, {
  useContext, useEffect, useReducer
} from 'react'
import { GridItem } from '@zuto/core/src/grid'
import { EmploymentHistoryContext, areEmployerDetailsComplete, EmploymentHistoryEntry } from '../EmploymentHistoryContext'
import EmployerDetails from '../EmployerDetails'
import EmploymentStatus from '../EmploymentStatus'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import EmploymentHistoryEntryContext, { EmploymentSubQuestion } from './EmploymentHistoryEntryContext'
import TimeInEmploymentSituation from '../TimeInEmploymentSituation'
import EmploymentHistoryEntryReducer from './EmploymentHistoryEntryReducer'
import { areEmployerDetailsRequired } from '../EmploymentStatus/EmploymentStatusOptions'
import { HistoryEntryProps } from '../../HistoryEntry/HistoryEntryProps'
import { fireReactGAEvent } from '../../utils'
import { PrefillReviewPageApplicationFormContext } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import EmploymentStatusDropdown from '../EmploymentStatusDropdown'

const EmploymentEntry = ({
  entryId,
  disablePrevious,
  existingEntry,
  startAtEnd = false,
  updateFollowingPrefillStep
}: HistoryEntryProps<EmploymentHistoryEntry>): JSX.Element => {
  const { setQuestionName } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch: employmentHistoryDispatch } = useContext(EmploymentHistoryContext)
  const { featureSwitches } = useContext(ApplicationFormContext)
  const {
    reviewPageV3
  } = featureSwitches ?? {}

  const calculateStartingQuestion = (): EmploymentSubQuestion => {
    if (reviewPageV3) return 'SITUATION'
    if (existingEntry === undefined) return 'SITUATION'
    if (startAtEnd) return 'TIME_IN_SITUATION'
    if (existingEntry.entrySource === 'FORM') {
      return 'SITUATION'
    }
    if (!existingEntry.employmentSituation) return 'SITUATION'
    if (areEmployerDetailsRequired(existingEntry.employmentSituation) && !areEmployerDetailsComplete(existingEntry.employerDetails)) return 'EMPLOYER_DETAILS'
    if (!existingEntry.lengthOfTime) return 'TIME_IN_SITUATION'
    return 'SITUATION'
  }

  const initialState = {
    entry: existingEntry ?? { entrySource: 'FORM' },
    question: calculateStartingQuestion()
  }

  const [state, dispatch] = useReducer(EmploymentHistoryEntryReducer(), initialState)

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('employmentHistory')
    }
  }, [])

  useEffect(() => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label: `qst.employmentHistory.${parseInt(entryId, 10) - 1}`,
      nonInteraction: true
    })
  }, [])

  useEffect(() => {
    switch (state.escapeAction) {
      case 'NEXT': {
        employmentHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        employmentHistoryDispatch({
          type: 'NEXT'
        })
        break
      }
      case 'PREVIOUS': {
        employmentHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        employmentHistoryDispatch({
          type: 'PREVIOUS'
        })
        break
      }
      default:
        break
    }
  }, [state.escapeAction])

  const renderQuestion = (): JSX.Element => {
    switch (state.question) {
      case 'SITUATION':
        return (
          <EmploymentStatus
            existingSituation={state.entry.employmentSituation}
            entryId={entryId}
            disablePrevious={disablePrevious}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      case 'EMPLOYER_DETAILS':
        return (
          <EmployerDetails
            existingAnswers={{
              employerDetails: state.entry.employerDetails,
              lengthOfTime: state.entry.lengthOfTime
            }}
            entryId={entryId}
            includeTimeInSituationFields={false}
          />
        )
      case 'TIME_IN_SITUATION':
        return (
          <TimeInEmploymentSituation
            existingDuration={state.entry.lengthOfTime}
            entryId={entryId}
            situation={state.entry.employmentSituation}
            employer={state.entry.employerDetails?.companyName}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      case 'EMPLOYMENT_QUESTIONS_GROUPED':
        return (
          <>
            <EmploymentStatusDropdown
              existingSituation={state.entry.employmentSituation}
              entryId={entryId}
            />
            <EmployerDetails
              existingAnswers={{
                employerDetails: state.entry.employerDetails,
                lengthOfTime: state.entry.lengthOfTime
              }}
              entryId={entryId}
              includeTimeInSituationFields={!!state.entry.employmentSituation}
              areEmploymentDetailsRequired={areEmployerDetailsRequired(state.entry.employmentSituation)}
            />
          </>
        )
      default:
        // It shouldn't be anything else
        return <div>Oops, something went wrong</div>
    }
  }
  return (
    <EmploymentHistoryEntryContext.Provider value={{ dispatch }}>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']}>
        {renderQuestion()}
      </GridItem>
    </EmploymentHistoryEntryContext.Provider>
  )
}

export default EmploymentEntry
